/**
 * EncounterFilterPills
 *
 * Displays active filters as removable chips/pills.
 * Enterprise design with refined pill styling.
 */

import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';
import { ActiveFilter, EncounterListFilters } from '../types';

interface FilterPillProps {
  filter: ActiveFilter;
  onRemove: () => void;
}

function FilterPill({ filter, onRemove }: FilterPillProps) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
      <span className="text-blue-500">{filter.label}:</span>
      <span className="max-w-[150px] truncate font-semibold">{filter.displayValue}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 p-0.5 rounded hover:bg-blue-100 transition-colors"
        aria-label={`Remove ${filter.label} filter`}
      >
        <XMarkIcon className="h-3 w-3" />
      </button>
    </span>
  );
}

export function EncounterFilterPills() {
  const { activeFilters, removeFilter, clearFilters } = useEncounterList();

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 flex-wrap pt-3 mt-3 border-t border-gray-100">
      <span className="text-xs text-gray-500 font-medium">Active:</span>

      {activeFilters.map((filter) => (
        <FilterPill
          key={filter.key}
          filter={filter}
          onRemove={() => removeFilter(filter.key as keyof EncounterListFilters)}
        />
      ))}

      <button
        type="button"
        onClick={clearFilters}
        className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 hover:bg-red-50 rounded-lg transition-colors"
      >
        Clear All
      </button>
    </div>
  );
}

export default EncounterFilterPills;
